'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { LightCard } from '@algoacademy/design-system/web/data-display/Card';
import { CalendarWrapper } from '@algoacademy/design-system/web/forms/Calendar';
import { VerticalSpacer } from '@algoacademy/design-system/web/layouts/VerticalSpacer';
import { P3 } from '@algoacademy/design-system/web/typography/Paragraph';
import { addDays, format, isBefore, parse, startOfDay, subDays } from 'date-fns';
import { useMemo, useState } from 'react';
import { useSeedSelectedEventsMapWithEventsAvailableForBooking } from '@/app/(marketing-site)/_hooks/useSeedSelectedEventsMapWithEventsAvailableForBooking';
import { type CalendarEvent, Event } from '@/app/(marketing-site)/bezplatna-konsultacja/_components/CalendarEvent';
import { CalendarHeader } from '@/app/(marketing-site)/bezplatna-konsultacja/_components/CalendarHeader';
import { LoadingSkeleton } from '@/app/(marketing-site)/bezplatna-konsultacja/_components/LoadingSkeleton';
type BookPhoneCallProps = {
  selectedDay: Date;
  setSelectedDay: (day: Date) => void;
  selectedEvent: CalendarEvent | null;
  setSelectedEvent: (event: CalendarEvent) => void;
  activeStepIdx: number;
  setActiveStepIdx: (idx: number) => void;
};
export function CalendarSection(t0) {
  const $ = _c(46);
  const {
    selectedDay,
    setSelectedDay,
    selectedEvent,
    setSelectedEvent,
    activeStepIdx,
    setActiveStepIdx
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [eventsMap, setEventsMap] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      setAvailableForBookingEventsMap: setEventsMap
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    isLoading
  } = useSeedSelectedEventsMapWithEventsAvailableForBooking(t2);
  let days;
  let t3;
  if ($[2] !== eventsMap || $[3] !== selectedDay) {
    const now = new Date();
    const today = startOfDay(now);
    days = [...eventsMap.keys()].filter(dateStr => !isBefore(parse(dateStr, "yyyy.MM.dd", now), today));
    const dayKey = format(selectedDay, "yyyy.MM.dd");
    t3 = eventsMap.get(dayKey)?.filter(event => !isBefore(event.startTime, now)) ?? [];
    $[2] = eventsMap;
    $[3] = selectedDay;
    $[4] = days;
    $[5] = t3;
  } else {
    days = $[4];
    t3 = $[5];
  }
  const eventsToday = t3;
  let t4;
  if ($[6] !== days) {
    t4 = new Set(days);
    $[6] = days;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== eventsToday || $[9] !== t4) {
    t5 = {
      bookingDays: t4,
      eventsForSelectedDay: eventsToday
    };
    $[8] = eventsToday;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    bookingDays,
    eventsForSelectedDay
  } = t5;
  if (isLoading) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <LoadingSkeleton />;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <VerticalSpacer size="paragraph" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] !== selectedDay || $[14] !== setSelectedDay) {
    t7 = () => setSelectedDay(addDays(selectedDay, 1));
    t8 = () => !isBefore(new Date(), selectedDay) && setSelectedDay(subDays(selectedDay, 1));
    $[13] = selectedDay;
    $[14] = setSelectedDay;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] !== selectedDay || $[18] !== t7 || $[19] !== t8) {
    t9 = <CalendarHeader selectedDaySlot={selectedDay} handleNextDay={t7} handlePrevDay={t8} />;
    $[17] = selectedDay;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== setSelectedDay) {
    t10 = day => day && setSelectedDay(day);
    $[21] = setSelectedDay;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== bookingDays || $[24] !== selectedDay || $[25] !== t10) {
    t11 = <CalendarWrapper className="w-fit" mode="single" selected={selectedDay} onSelect={t10} availableDays={bookingDays} disableUnavailableDays={true} required={true} />;
    $[23] = bookingDays;
    $[24] = selectedDay;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== eventsForSelectedDay || $[28] !== selectedEvent?.id || $[29] !== setSelectedEvent) {
    t12 = <section className="flex-1"><LightCard className="space-y-4 p-6">{eventsForSelectedDay.length > 0 ? <section className="grid auto-rows-min grid-cols-3 gap-4 sm:grid-cols-4">{eventsForSelectedDay.map(event_0 => <Event key={event_0.id} event={event_0} isChecked={selectedEvent?.id === event_0.id} handleSelectedEvent={() => setSelectedEvent(event_0)} />)}</section> : <P3>W tym dniu nie mamy żadnych dostępnych terminów</P3>}</LightCard></section>;
    $[27] = eventsForSelectedDay;
    $[28] = selectedEvent?.id;
    $[29] = setSelectedEvent;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <section className="flex flex-col gap-4 rounded-b-lg border border-t-0 p-4 lg:flex-row lg:p-6">{t11}{t12}</section>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13 || $[35] !== t9) {
    t14 = <LightCard className="flex flex-col border-none bg-transparent p-0">{t9}{t13}</LightCard>;
    $[34] = t13;
    $[35] = t9;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== activeStepIdx || $[38] !== setActiveStepIdx) {
    t15 = () => setActiveStepIdx(activeStepIdx + 1);
    $[37] = activeStepIdx;
    $[38] = setActiveStepIdx;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const t16 = !selectedEvent;
  let t17;
  if ($[40] !== t15 || $[41] !== t16) {
    t17 = <div className="flex flex-row justify-end"><Button size="xl" onClick={t15} disabled={t16}>Przejdź dalej</Button></div>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t14 || $[44] !== t17) {
    t18 = <>{t6}<main className="space-y-8">{t14}{t17}</main></>;
    $[43] = t14;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
}